/**
 * Extracts words from a string in order.
 * @param {string} str - The input string.
 * @returns {string[]} An array of words from the string in order.
 */
function getWordsInOrder(str: string): string[] {
  if (!str || typeof str !== 'string' || str.trim() === '') {
    return [];
  }
  let processedStr = str.replace(/<[^>]*>/g, ' ');
  processedStr = processedStr.replace(/[^a-zA-Z0-9\s]/g, ' ');
  const lowercasedStr = processedStr.toLowerCase();
  return lowercasedStr.split(/\s+/);
};


/**
 * Calculates the Levenshtein distance between two arrays of strings (words).
 * The distance is the minimum number of single-word edits (insertions,
 * deletions, or substitutions) required to change one sequence into the other.
 *
 * @param {string[]} wordSequence1 - The first array of words.
 * @param {string[]} wordSequence2 - The second array of words.
 * @returns {number} The Levenshtein distance between the two word sequences.
 */
function calculateLevenshteinDistance(wordSequence1: string[], wordSequence2: string[]): number {
  const len1 = wordSequence1.length;
  const len2 = wordSequence2.length;
  const distances: number[][] = Array(len1 + 1).fill(0).map(() => Array(len2 + 1).fill(0));
  for (let i = 0; i <= len1; i++) {
    distances[i][0] = i;
  }
  for (let j = 0; j <= len2; j++) {
    distances[0][j] = j;
  }
  for (let i = 1; i <= len1; i++) {
    const word1 = wordSequence1[i - 1];
    for (let j = 1; j <= len2; j++) {
      const word2 = wordSequence2[j - 1];
      const substitutionCost = word1 === word2 ? 0 : 1;
      const deletion = distances[i - 1][j] + 1;
      const insertion = distances[i][j - 1] + 1;
      const substitution = distances[i - 1][j - 1] + substitutionCost;
      distances[i][j] = Math.min(deletion, insertion, substitution);
    }
  }
  return distances[len1][len2];
}

/**
 * Checks if the Levenshtein distance (minimum word edits: insertions, deletions, substitutions)
 * between the word sequences of two strings is 3 or more.
 * This accounts for added words, removed words, and changes in word order/identity.
 *
 * @param {string} str1 - The original string.
 * @param {string} str2 - The modified string.
 * @returns {boolean} True if the Levenshtein distance between the word sequences
 * is 3 or greater, false otherwise.
 */
export const hasAtLeastThreeWordEdits = (str1: string, str2: string): boolean => {
  const words1 = getWordsInOrder(str1);
  const words2 = getWordsInOrder(str2);
  const distance = calculateLevenshteinDistance(words1, words2);
  return distance >= 3;
};
